import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Axios from 'axios';
import moment from 'moment';
import { FaPlus, FaSun, FaCouch, FaGraduationCap, FaCog, FaFileExcel, FaFileAlt, FaSpinner, FaPencilAlt, FaTrash, } from 'react-icons/fa';
import SimplePanel from '@/shared/components/SimplePanel';
import ScrollableTable from '@/shared/components/table/ScrollableTable';
import ColumnHeader from '@/shared/components/table/ColumnHeader';
import Checkbox from '@/shared/components/input/Checkbox';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import { formatListValue } from '@/shared/components/FormattedListValue';
import { initHiddenColumnsState, handleHiddenColumnsSelection, } from '@/shared/utils/hiddenColumnsHelper';
import { useStateService } from '@/shared/services/StateService';
import RegistrationModal from '@/shared/components/registration/RegistrationModal';
import RegistrationGroupModal from '@/shared/components/registration/RegistrationGroupModal';
import { absenceRegistrationOptions, trainingRegistrationOptions, } from '@/shared/components/registration/registrationHelpers';
import AuthService, { functionalities, privileges } from '@/shared/services/AuthService';
import DataCacheService from '@/shared/services/DataCacheService';
import SignalRService from '@/shared/services/SignalRService';
import ToastrService from '@/shared/services/ToastrService';
import YesNoModal from '@/shared/components/YesNoModal';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import { mapResourceName } from '@/shared/services/ObjectDisplayService';
import TaskDetailsModal from '@/modules/tasks/taskDetails/TaskDetailsModal';
import { guid1 } from '@/shared/utils/guid';
import RegistrationOverviewFilters from './RegistrationOverviewFilters';
import RegistrationKpiHeader from './RegistrationKpiHeader';
import RegistrationOverviewRow from './RegistrationOverviewRow';
import DeleteWithNeverInvoiceModal from './modals/DeleteWithNeverInvoiceModal';
import BatchUpdateRegistrationFieldModal from './modals/BatchUpdateRegistrationFieldModal';
import RegistrationInvoiceDetailReportModal from './modals/RegistrationInvoiceDetailReportModal';
import messages from './messages';
import { buildServerFilters } from './registrationOverviewFilterState';
import { buildInitialFilterState, saveSessionParams } from './registrationOverviewStorage';
import { addRegistrationToDraft, batchBill, batchDelete, batchToggleLock, batchUnbill, createReport, deleteRegistration, removeRegistrationFromDraft, retrieveKpis, retrieveRegistrations, toggleBillable, toggleInForfait, toggleLock, toggleNeverInvoice, } from './registrationOverviewApi';
const REGISTRATION_JOB_TYPES = [38, 62, 66, 67, 68, 76, 77, 78];
const RegistrationOverviewContainer = ({ searchParams: stateParams }) => {
    var _a, _b;
    const intl = useIntl();
    const currentUserId = AuthService.getUserId();
    const canViewOthers = AuthService.hasPermission({
        functionality: functionalities.RegistrationOthers,
    });
    const canEditOthers = AuthService.hasPermission({
        functionality: functionalities.RegistrationOthers,
        privilege: privileges.Update,
    });
    const canCreate = AuthService.hasPermission({
        functionality: functionalities.RegistrationOwn,
        privilege: privileges.Create,
    });
    const canCreateOthers = AuthService.hasPermission({
        functionality: functionalities.RegistrationOthers,
        privilege: privileges.Create,
    });
    const canDeleteOwn = AuthService.hasPermission({
        functionality: functionalities.RegistrationOwn,
        privilege: privileges.Delete,
    });
    const canDeleteOthers = AuthService.hasPermission({
        functionality: functionalities.RegistrationOthers,
        privilege: privileges.Delete,
    });
    const canReadPrices = AuthService.hasPermission({ functionality: functionalities.Pricelist });
    const canReadInvoiceDraft = AuthService.hasPermission({
        functionality: functionalities.InvoiceDraft,
    });
    const canUpdateInvoiceDraft = AuthService.hasPermission({
        functionality: functionalities.InvoiceDraft,
        privilege: privileges.Update,
    });
    const canUpdateOwn = AuthService.hasPermission({
        functionality: functionalities.RegistrationOwn,
        privilege: privileges.Update,
    });
    const canCreateInvoiceDraft = AuthService.hasPermission({
        functionality: functionalities.InvoiceDraft,
        privilege: privileges.Create,
    });
    const canDeleteInvoiceDraft = AuthService.hasPermission({
        functionality: functionalities.InvoiceDraft,
        privilege: privileges.Delete,
    });
    const canEditTask = AuthService.hasPermission({
        functionality: functionalities.Todo,
        privilege: privileges.Update,
    }) &&
        AuthService.hasPermission({
            functionality: functionalities.PlannedTask,
            privilege: privileges.Update,
        });
    const hasReferenceYear = AuthService.hasFeature('ReferenceYear');
    const assignmentsEnabled = hasAssignments();
    const allowRegistrationLocking = !!((_a = DataCacheService.getDefaultCompany()) === null || _a === void 0 ? void 0 : _a.allowRegistrationLocking);
    const initRef = useRef(buildInitialFilterState(stateParams, canViewOthers, currentUserId));
    const [filterState, setFilterState] = useState(initRef.current.filterState);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [kpis, setKpis] = useState(null);
    const [selectAll, setSelectAll] = useState(false);
    const [modal, setModal] = useState(null);
    const [groupModal, setGroupModal] = useState(null);
    const [initialSearchParams, setInitialSearchParams] = useState(() => {
        var _a, _b, _c, _d;
        const tp = initRef.current.initialTableParams;
        return {
            page: 0,
            pageSize: 50,
            desc: (_a = tp === null || tp === void 0 ? void 0 : tp.desc) !== null && _a !== void 0 ? _a : true,
            sortKey: (_b = tp === null || tp === void 0 ? void 0 : tp.sortKey) !== null && _b !== void 0 ? _b : 'date,fromMinutes',
            filter: (_c = tp === null || tp === void 0 ? void 0 : tp.filter) !== null && _c !== void 0 ? _c : {},
            search: (_d = tp === null || tp === void 0 ? void 0 : tp.search) !== null && _d !== void 0 ? _d : {},
            serverFilters: buildServerFilters(initRef.current.filterState),
            nonce: 0,
        };
    });
    const [manipulatingDrafts, setManipulatingDrafts] = useState(false);
    const [globalActionRunning, setGlobalActionRunning] = useState(false);
    const [creatingReport, setCreatingReport] = useState(false);
    const [showColumnModal, setShowColumnModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [removeFromDraftTarget, setRemoveFromDraftTarget] = useState(null);
    const [batchDeleteOpen, setBatchDeleteOpen] = useState(false);
    const [batchLockOpen, setBatchLockOpen] = useState(null);
    const [batchBillOpen, setBatchBillOpen] = useState(false);
    const [batchUnbillOpen, setBatchUnbillOpen] = useState(false);
    const [batchUpdateOpen, setBatchUpdateOpen] = useState(false);
    const [detailReportOpen, setDetailReportOpen] = useState(false);
    const [taskDetail, setTaskDetail] = useState(null);
    const stateService = useStateService();
    const selectAllRef = useRef(false);
    const manipulatingDraftsRef = useRef(false);
    const listCancelRef = useRef(null);
    const kpiCancelRef = useRef(null);
    const currentTableParamsRef = useRef(null);
    const currentServerFiltersRef = useRef(null);
    const setManipulating = (value) => {
        manipulatingDraftsRef.current = value;
        setManipulatingDrafts(value);
    };
    const permissions = useMemo(() => ({
        canReadPrices,
        canReadInvoiceDraft,
        canUpdateInvoiceDraft,
        canEditOthers,
        canDeleteOwn,
        canDeleteOthers,
        allowRegistrationLocking,
    }), [
        canReadPrices,
        canReadInvoiceDraft,
        canUpdateInvoiceDraft,
        canEditOthers,
        canDeleteOwn,
        canDeleteOthers,
        allowRegistrationLocking,
    ]);
    const getUserName = (userId) => userId === guid1
        ? intl.formatMessage(messages.SystemUser)
        : DataCacheService.getUserName(userId) || '';
    const mapRow = (reg) => {
        var _a;
        return (Object.assign(Object.assign({}, reg), { user: getUserName(reg.userId), resourceName: (_a = reg.resourceName) !== null && _a !== void 0 ? _a : mapResourceName(reg.resource, intl), selected: selectAllRef.current }));
    };
    const refreshKpis = (tableParams, serverFilters) => {
        if (kpiCancelRef.current)
            kpiCancelRef.current.cancel('new kpi request');
        kpiCancelRef.current = Axios.CancelToken.source();
        retrieveKpis(tableParams, serverFilters, kpiCancelRef.current.token)
            .then(data => {
            setKpis(data);
            setTotal(data.totalRowCount || 0);
        })
            .catch(error => {
            if (!Axios.isCancel(error))
                throw error;
        });
    };
    const loadItems = (searchParams) => {
        const tableParams = {
            page: searchParams.page,
            pageSize: searchParams.pageSize,
            desc: searchParams.desc,
            sortKey: searchParams.sortKey,
            filter: searchParams.filter,
            search: searchParams.search,
            select: searchParams.select,
        };
        const { serverFilters } = searchParams;
        currentTableParamsRef.current = tableParams;
        currentServerFiltersRef.current = serverFilters;
        saveSessionParams(filterState, {
            page: tableParams.page,
            pageSize: tableParams.pageSize,
            sortKey: tableParams.sortKey,
            desc: tableParams.desc,
            search: tableParams.search,
            filter: tableParams.filter,
        });
        if (listCancelRef.current)
            listCancelRef.current.cancel('new request');
        listCancelRef.current = Axios.CancelToken.source();
        return retrieveRegistrations(tableParams, serverFilters, listCancelRef.current.token)
            .then(data => {
            const newRows = data.map(mapRow);
            setRows(prev => (searchParams.page > 0 ? [...prev, ...newRows] : newRows));
            if (searchParams.page === 0)
                refreshKpis(tableParams, serverFilters);
            return newRows;
        })
            .catch(error => {
            if (!Axios.isCancel(error))
                return error;
            return undefined;
        });
    };
    const reloadTable = () => setInitialSearchParams(sp => (Object.assign(Object.assign({}, sp), { page: 0, serverFilters: buildServerFilters(filterState), nonce: sp.nonce + 1 })));
    const reloadTableRef = useRef(reloadTable);
    reloadTableRef.current = reloadTable;
    useEffect(() => {
        const handler = (job) => {
            if (REGISTRATION_JOB_TYPES.includes(job.jobType) && (job.status === 2 || job.status === 3)) {
                setManipulating(false);
                reloadTableRef.current();
            }
        };
        SignalRService.subscribe('jobUpdated', handler);
        return () => {
            SignalRService.unsubscribe('jobUpdated', handler);
            if (listCancelRef.current)
                listCancelRef.current.cancel('leaving page');
            if (kpiCancelRef.current)
                kpiCancelRef.current.cancel('leaving page');
        };
    }, []);
    const handleFilterChange = (patch) => {
        const next = Object.assign(Object.assign({}, filterState), patch);
        setFilterState(next);
        setInitialSearchParams(sp => (Object.assign(Object.assign({}, sp), { page: 0, serverFilters: buildServerFilters(next), nonce: sp.nonce + 1 })));
    };
    // Replaces the whole filter state at once (saved-filter apply or toolbar reset), also
    // clearing the table's own column filters and selection.
    const handleApplyFilters = (next) => {
        var _a;
        const applied = Object.assign(Object.assign({}, next), { userId: canViewOthers ? (_a = next.userId) !== null && _a !== void 0 ? _a : null : currentUserId });
        selectAllRef.current = false;
        setSelectAll(false);
        setFilterState(applied);
        setInitialSearchParams(sp => (Object.assign(Object.assign({}, sp), { page: 0, filter: {}, search: {}, serverFilters: buildServerFilters(applied), nonce: sp.nonce + 1 })));
    };
    const handleSelectAll = () => {
        const next = !selectAll;
        selectAllRef.current = next;
        setSelectAll(next);
        setRows(prev => prev.map(r => (Object.assign(Object.assign({}, r), { selected: next }))));
    };
    const handleToggleSelect = (reg) => setRows(prev => prev.map(r => (r.id === reg.id ? Object.assign(Object.assign({}, r), { selected: !r.selected }) : r)));
    const patchRow = (id, patch) => setRows(prev => prev.map(r => (r.id === id ? Object.assign(Object.assign({}, r), patch) : r)));
    const remapRow = (id, updated) => setRows(prev => prev.map(r => (r.id === id ? Object.assign(Object.assign({}, mapRow(updated)), { selected: r.selected }) : r)));
    const refreshKpisNow = () => {
        if (currentTableParamsRef.current && currentServerFiltersRef.current) {
            refreshKpis(currentTableParamsRef.current, currentServerFiltersRef.current);
        }
    };
    const handleToggleBillable = (reg, checked) => {
        if (reg.invoiceStatus > 0)
            return Promise.resolve();
        return toggleBillable(reg.id, checked)
            .then(data => patchRow(reg.id, { invoiceStatus: data.invoiceStatus, isBillable: data.isBillable }))
            .catch(error => ToastrService.error(error));
    };
    const handleToggleInForfait = (reg, checked) => {
        if (reg.invoiceStatus > 0)
            return Promise.resolve();
        return toggleInForfait(reg.id, checked)
            .then(data => patchRow(reg.id, { invoiceStatus: data.invoiceStatus, inForfait: data.inForfait }))
            .catch(error => ToastrService.error(error));
    };
    const handleToggleNeverInvoice = (reg, checked) => {
        if (reg.invoiceStatus > 0)
            return Promise.resolve();
        return toggleNeverInvoice(reg.id, checked)
            .then(data => patchRow(reg.id, { invoiceStatus: data.invoiceStatus, neverInvoice: data.neverInvoice }))
            .catch(error => ToastrService.error(error));
    };
    const handleToggleLock = (reg) => {
        patchRow(reg.id, { toggling: true });
        toggleLock(reg.id, !reg.isLocked)
            .then(data => data
            ? remapRow(reg.id, Object.assign(Object.assign({}, data), { toggling: false }))
            : patchRow(reg.id, { toggling: false }))
            .catch(() => patchRow(reg.id, { toggling: false }));
    };
    const handleAddToDraft = (reg) => {
        if (manipulatingDraftsRef.current)
            return;
        setManipulating(true);
        patchRow(reg.id, { addingToDraft: true });
        addRegistrationToDraft(reg.id)
            .then(data => {
            setManipulating(false);
            if (data && data.id === reg.id && data.invoiceStatus === 1) {
                remapRow(reg.id, Object.assign(Object.assign({}, data), { addingToDraft: false }));
                ToastrService.success(intl.formatMessage(messages.AddedRegistrationToDraft));
            }
            else {
                patchRow(reg.id, { addingToDraft: false });
                if (data && data.invoiceStatus === 0) {
                    ToastrService.info(intl.formatMessage(messages.NoInvoiceCreated));
                }
            }
        })
            .catch(error => {
            setManipulating(false);
            patchRow(reg.id, { addingToDraft: false });
            ToastrService.error(error);
        });
    };
    const resolveRemoveFromDraft = (neverInvoice) => {
        const reg = removeFromDraftTarget;
        setRemoveFromDraftTarget(null);
        if (neverInvoice === null || !reg)
            return;
        patchRow(reg.id, { removingFromDraft: true });
        removeRegistrationFromDraft(reg.invoiceId, reg.id, neverInvoice)
            .then(result => {
            if (result && result.updatedRegistration && result.updatedRegistration.id === reg.id) {
                remapRow(reg.id, Object.assign(Object.assign({}, result.updatedRegistration), { removingFromDraft: false }));
                ToastrService.success(intl.formatMessage(messages.RemovedRegistrationFromDraft));
            }
            else {
                patchRow(reg.id, { removingFromDraft: false });
            }
        })
            .catch(error => {
            patchRow(reg.id, { removingFromDraft: false });
            ToastrService.error(error);
        });
    };
    const confirmDelete = () => {
        const reg = deleteTarget;
        setDeleteTarget(null);
        if (!reg)
            return Promise.resolve();
        patchRow(reg.id, { removing: true });
        return deleteRegistration(reg.id)
            .then(() => {
            setRows(prev => prev.filter(r => r.id !== reg.id));
            refreshKpisNow();
        })
            .catch(error => {
            patchRow(reg.id, { removing: false });
            ToastrService.error(error);
        });
    };
    const handleDelete = (reg) => {
        if (reg.isLocked) {
            if (!allowRegistrationLocking) {
                ToastrService.error(intl.formatMessage(messages.RegistrationIsLockedForDelete));
            }
            return;
        }
        setDeleteTarget(reg);
    };
    const getBatchSelectionParams = () => ({
        selectAll,
        ids: rows.filter(r => !!r.selected !== selectAll).map(r => r.id),
    });
    const onJobStarted = (data) => {
        if (data && data.id) {
            ToastrService.info(intl.formatMessage(messages.JobStarted), undefined, () => stateService.gotoJob(data.id));
        }
    };
    const runBatchJob = (apiCall) => {
        const tableParams = currentTableParamsRef.current;
        const filters = currentServerFiltersRef.current;
        if (!tableParams || !filters)
            return;
        setGlobalActionRunning(true);
        setManipulating(true);
        apiCall(getBatchSelectionParams(), tableParams, filters)
            .then(data => {
            setGlobalActionRunning(false);
            onJobStarted(data);
        })
            .catch(error => {
            setGlobalActionRunning(false);
            setManipulating(false);
            ToastrService.error(error);
        });
    };
    const confirmBatchDelete = () => {
        setBatchDeleteOpen(false);
        runBatchJob((selection, tableParams, filters) => batchDelete(selection, tableParams, filters));
    };
    const confirmBatchLock = () => {
        var _a;
        const lock = (_a = batchLockOpen === null || batchLockOpen === void 0 ? void 0 : batchLockOpen.lock) !== null && _a !== void 0 ? _a : false;
        setBatchLockOpen(null);
        runBatchJob((selection, tableParams, filters) => batchToggleLock(selection, tableParams, filters, lock));
    };
    const confirmBatchBill = () => {
        setBatchBillOpen(false);
        runBatchJob((selection, tableParams, filters) => batchBill(selection, tableParams, filters));
    };
    const resolveBatchUnbill = (neverInvoice) => {
        setBatchUnbillOpen(false);
        if (neverInvoice === null)
            return;
        runBatchJob((selection, tableParams, filters) => batchUnbill(selection, tableParams, filters, neverInvoice));
    };
    const createExcelReport = () => {
        const tableParams = currentTableParamsRef.current;
        const filters = currentServerFiltersRef.current;
        if (!tableParams || !filters)
            return;
        setCreatingReport(true);
        createReport(tableParams, filters, intl.locale)
            .then(() => {
            setCreatingReport(false);
            ToastrService.success(intl.formatMessage(messages.ExportToExcelStarted));
        })
            .catch(error => {
            setCreatingReport(false);
            ToastrService.error(error);
        });
    };
    const canUpdateReg = (reg) => {
        if (reg.invoiceStatus > 1)
            return false;
        if (reg.userId === currentUserId)
            return true;
        return canEditOthers;
    };
    const handleBatchUpdateSaved = (result) => {
        setBatchUpdateOpen(false);
        if (!result || !result.count)
            return;
        const bool = !!result.boolValue;
        // Reference year (8) and relation/assignment/resource (5/6/7) run as background jobs
        // that reload via SignalR; the simple boolean fields patch the visible rows in place.
        if (result.field === 8) {
            reloadTable();
            return;
        }
        if (![1, 2, 3].includes(result.field))
            return;
        setRows(prev => prev.map(reg => {
            if (!canUpdateReg(reg) || !reg.selected)
                return reg;
            if (result.field === 1)
                return Object.assign(Object.assign({}, reg), { neverInvoice: bool });
            if (result.field === 2)
                return Object.assign(Object.assign({}, reg), { isBillable: bool });
            if (result.field === 3)
                return reg.inForfait === null ? reg : Object.assign(Object.assign({}, reg), { inForfait: bool });
            return reg;
        }));
    };
    const openDetailReport = () => {
        var _a;
        if (!((_a = filterState.relation) === null || _a === void 0 ? void 0 : _a.id)) {
            ToastrService.warning(intl.formatMessage(messages.PleaseFilterOnRelation));
            return;
        }
        setDetailReportOpen(true);
    };
    const selectedCount = useMemo(() => {
        const count = rows.filter(r => !!r.selected !== selectAll).length;
        return selectAll ? total - count : count;
    }, [rows, selectAll, total]);
    const today = () => moment
        .utc()
        .startOf('day')
        .toDate();
    const openEdit = (reg) => {
        var _a, _b;
        const type = (_a = reg.type) !== null && _a !== void 0 ? _a : (_b = reg.resource) === null || _b === void 0 ? void 0 : _b.type;
        if (type === 3 || type === 4) {
            setModal({ registration: reg, registrationOptions: absenceRegistrationOptions });
            return;
        }
        if (type === 5) {
            setModal({ registration: reg, registrationOptions: trainingRegistrationOptions });
            return;
        }
        // Time / product-cost / distance are edited in the grouped dialog, together with the
        // sibling registrations that were created alongside it (same group).
        const siblings = reg.group ? rows.filter(r => r.group && r.group === reg.group) : [reg];
        setGroupModal({ registrations: siblings.length ? siblings : [reg] });
    };
    const createRegistration = (userId) => {
        const seed = { userId: userId || currentUserId, date: today() };
        if (filterState.relation && filterState.relation.crm !== 0) {
            seed.relation = filterState.relation;
            seed.invoiceStreamId = filterState.relation.invoiceStreamId || null;
        }
        // The grouped dialog lets work, product/cost and distance be entered together.
        setGroupModal({ registration: seed });
    };
    const createAbsence = (type, userId) => setModal({
        registration: { type, userId: userId || currentUserId, date: today() },
        registrationOptions: absenceRegistrationOptions,
    });
    const createTraining = (userId) => setModal({
        registration: { type: 5, userId: userId || currentUserId, date: today() },
        registrationOptions: trainingRegistrationOptions,
    });
    const handleModalSave = () => {
        setModal(null);
        reloadTable();
    };
    const showRecuperation = DataCacheService.timeTablesEnabled();
    const selectedUserId = filterState.userId;
    const showForUser = canCreateOthers && !!selectedUserId && selectedUserId !== currentUserId;
    const selectedUserName = selectedUserId ? getUserName(selectedUserId) : '';
    const columnDefinitions = useMemo(() => [
        { id: 'date', message: messages.Date },
        { id: 'referenceYear', message: messages.ReferenceYear, enabled: () => hasReferenceYear },
        { id: 'user', message: messages.User },
        { id: 'relation', message: messages.Relation },
        { id: 'customerCode', message: messages.CustomerCode },
        { id: 'assignment', message: messages.Assignment, enabled: () => assignmentsEnabled },
        { id: 'resourceType', message: messages.Type },
        { id: 'resource', message: messages.Resource },
        { id: 'trainingName', message: messages.Training },
        { id: 'taskName', message: messages.Task },
        { id: 'fromMinutes', message: messages.From },
        { id: 'toMinutes', message: messages.To },
        { id: 'quantity', message: messages.Quantity },
        { id: 'unitPrice', message: messages.UnitPrice, enabled: () => canReadPrices },
        { id: 'vatPercentage', message: messages.VatPercent, enabled: () => canReadPrices },
        { id: 'totalAmount', message: messages.Amount, enabled: () => canReadPrices },
        {
            id: 'isFixedPrice',
            message: messages.IsFixedPrice,
            visibleByDefault: false,
            enabled: () => canReadPrices,
        },
        { id: 'isBillable', message: messages.Billable, enabled: () => canReadPrices },
        { id: 'forfaitStatus', message: messages.InForfait, enabled: () => canReadPrices },
        {
            id: 'invoiceForfaitStatus',
            message: messages.InvoiceInForfait,
            visibleByDefault: false,
            enabled: () => canReadInvoiceDraft,
        },
        { id: 'neverInvoice', message: messages.NeverInvoice, enabled: () => canReadInvoiceDraft },
        { id: 'invoiceStatus', message: messages.Invoice, enabled: () => canReadInvoiceDraft },
        { id: 'remark', message: messages.Remark },
    ], [hasReferenceYear, assignmentsEnabled, canReadPrices, canReadInvoiceDraft]);
    const [columnState, setColumnState] = useState(() => initHiddenColumnsState('RegistrationOverviewHiddenColumns', columnDefinitions, intl));
    const { hideableColumns, columnStatus } = columnState;
    const handleColumnSelection = (items) => {
        setColumnState(handleHiddenColumnsSelection(items, hideableColumns, 'RegistrationOverviewHiddenColumns'));
        setShowColumnModal(false);
    };
    const renderYesNo = (item) => intl.formatMessage(item ? messages.Yes : messages.No);
    const renderForfait = (item) => {
        if (item === 2)
            return intl.formatMessage(messages.Yes);
        if (item === 1)
            return intl.formatMessage(messages.No);
        return intl.formatMessage(messages.NotApplicable);
    };
    // Use formatListValue (with the captured intl) rather than <FormattedListValue>, whose own
    // injectIntl context isn't available inside the filter popover's portal.
    const renderResourceType = (item) => formatListValue({ list: 'resourceType', id: item, intl });
    const renderInvoiceStatusItem = (item) => formatListValue({ list: 'RegistrationInvoiceStatus', id: item, intl });
    const addMenu = canCreate ? (_jsxs("div", Object.assign({ className: "btn-group" }, { children: [_jsxs("button", Object.assign({ type: "button", className: "btn btn-inverse", title: intl.formatMessage(messages.AddRegistration), onClick: () => createRegistration() }, { children: [_jsx(FaPlus, {}), " ", _jsx("span", { children: intl.formatMessage(messages.Add) })] })), _jsx("button", Object.assign({ type: "button", "data-toggle": "dropdown", "aria-haspopup": "true", "aria-expanded": "false", className: "btn btn-inverse dropdown-toggle" }, { children: _jsx("span", { className: "caret" }) })), _jsxs("ul", Object.assign({ className: "dropdown-menu dropdown-menu-right" }, { children: [_jsx("li", { children: _jsxs("a", Object.assign({ onClick: () => createRegistration() }, { children: [_jsx(FaPlus, {}), " ", _jsx("span", { children: intl.formatMessage(messages.AddRegistration) })] })) }), _jsx("li", { children: _jsxs("a", Object.assign({ onClick: () => createAbsence(3) }, { children: [_jsx(FaSun, {}), " ", _jsx("span", { children: intl.formatMessage(messages.AddAbsence) })] })) }), showRecuperation && (_jsx("li", { children: _jsxs("a", Object.assign({ onClick: () => createAbsence(4) }, { children: [_jsx(FaCouch, {}), " ", _jsx("span", { children: intl.formatMessage(messages.AddRecuperation) })] })) })), _jsx("li", { children: _jsxs("a", Object.assign({ onClick: () => createTraining() }, { children: [_jsx(FaGraduationCap, {}), " ", _jsx("span", { children: intl.formatMessage(messages.AddTraining) })] })) }), showForUser && _jsx("li", { role: "separator", className: "divider" }), showForUser && (_jsx("li", { children: _jsxs("a", Object.assign({ onClick: () => createRegistration(selectedUserId) }, { children: [_jsx(FaPlus, {}), ' ', _jsx("span", { children: intl.formatMessage(messages.AddRegistrationFor, { user: selectedUserName }) })] })) })), showForUser && selectedUserId !== guid1 && (_jsx("li", { children: _jsxs("a", Object.assign({ onClick: () => createAbsence(3, selectedUserId) }, { children: [_jsx(FaSun, {}), ' ', _jsx("span", { children: intl.formatMessage(messages.AddAbsenceFor, { user: selectedUserName }) })] })) })), showForUser && selectedUserId !== guid1 && showRecuperation && (_jsx("li", { children: _jsxs("a", Object.assign({ onClick: () => createAbsence(4, selectedUserId) }, { children: [_jsx(FaCouch, {}), ' ', _jsx("span", { children: intl.formatMessage(messages.AddRecuperationFor, { user: selectedUserName }) })] })) })), showForUser && selectedUserId !== guid1 && (_jsx("li", { children: _jsxs("a", Object.assign({ onClick: () => createTraining(selectedUserId) }, { children: [_jsx(FaGraduationCap, {}), ' ', _jsx("span", { children: intl.formatMessage(messages.AddTrainingFor, { user: selectedUserName }) })] })) }))] }))] }))) : null;
    const hasRows = rows.length > 0;
    const globalActionsDisabled = globalActionRunning || selectedCount === 0;
    const toolbar = (_jsx("div", Object.assign({ className: "row btn-row hidden-xs" }, { children: _jsxs("div", Object.assign({ className: "content-right" }, { children: [_jsxs("a", Object.assign({ className: "btn btn-xs btn-default margin10R", onClick: () => setShowColumnModal(true) }, { children: [_jsx(FaCog, {}), " ", _jsx("span", Object.assign({ className: "margin5L" }, { children: intl.formatMessage(messages.ColumnConfig) }))] })), hasRows && (_jsxs("div", Object.assign({ className: "dropdown margin10L inline-block" }, { children: [_jsxs("button", Object.assign({ type: "button", "data-toggle": "dropdown", disabled: creatingReport, "aria-haspopup": "true", "aria-expanded": "false", className: "btn btn-xs btn-default dropdown-toggle" }, { children: [creatingReport ? _jsx(FaSpinner, { className: "fa-spin" }) : _jsx(FaFileExcel, {}), ' ', _jsx("span", { children: intl.formatMessage(messages.Export) }), " ", _jsx("span", { className: "caret" })] })), _jsxs("ul", Object.assign({ className: "dropdown-menu dropdown-menu-right" }, { children: [_jsx("li", { children: _jsxs("a", Object.assign({ onClick: createExcelReport }, { children: [_jsx(FaFileExcel, { className: "fa-fw" }), ' ', _jsx("span", { children: intl.formatMessage(messages.ExportRegistrationsToExcel) })] })) }), _jsx("li", { children: _jsxs("a", Object.assign({ onClick: openDetailReport, title: ((_b = filterState.relation) === null || _b === void 0 ? void 0 : _b.id)
                                            ? intl.formatMessage(messages.ExportRegistrationsWithDetailTemplate)
                                            : intl.formatMessage(messages.PleaseFilterOnRelation) }, { children: [_jsx(FaFileAlt, { className: "fa-fw" }), ' ', _jsx("span", { children: intl.formatMessage(messages.ExportRegistrationsWithDetailTemplate) })] })) })] }))] }))), canUpdateOwn && hasRows && (_jsxs("div", Object.assign({ className: "dropdown margin10L inline-block" }, { children: [_jsxs("button", Object.assign({ type: "button", "data-toggle": "dropdown", disabled: globalActionsDisabled, "aria-haspopup": "true", "aria-expanded": "false", className: "btn btn-xs btn-inverse dropdown-toggle" }, { children: [globalActionRunning && _jsx(FaSpinner, { className: "fa-spin" }), ' ', _jsx("span", { children: intl.formatMessage(messages.GlobalActions) }), " ", _jsx("span", { className: "caret" })] })), _jsxs("ul", Object.assign({ className: "dropdown-menu dropdown-menu-right" }, { children: [_jsx("li", { children: _jsxs("a", Object.assign({ onClick: () => setBatchUpdateOpen(true) }, { children: [_jsx(FaPencilAlt, { className: "fa-fw" }), ' ', _jsx("span", { children: intl.formatMessage(messages.ModifyRegistration) })] })) }), canDeleteOwn && (_jsx("li", { children: _jsxs("a", Object.assign({ onClick: () => setBatchDeleteOpen(true) }, { children: [_jsx(FaTrash, { className: "fa-fw" }), ' ', _jsx("span", { children: intl.formatMessage(messages.DeleteRegistrations) })] })) })), canEditOthers && allowRegistrationLocking && (_jsx("li", { children: _jsxs("a", Object.assign({ onClick: () => setBatchLockOpen({ lock: true }) }, { children: [_jsx("i", { className: "fa fa-lock fa-fw" }), ' ', _jsx("span", { children: intl.formatMessage(messages.BatchLockRegistrations) })] })) })), canEditOthers && allowRegistrationLocking && (_jsx("li", { children: _jsxs("a", Object.assign({ onClick: () => setBatchLockOpen({ lock: false }) }, { children: [_jsx("i", { className: "fa fa-unlock fa-fw" }), ' ', _jsx("span", { children: intl.formatMessage(messages.BatchUnlockRegistrations) })] })) })), canCreateInvoiceDraft && (_jsx("li", { children: _jsxs("a", Object.assign({ onClick: () => setBatchBillOpen(true) }, { children: [_jsx("i", { className: "fa fa-money" }), " ", _jsx("i", { className: "fa fa-arrow-right margin5R" }), _jsx("span", { children: intl.formatMessage(messages.AddToInvoiceProposal) })] })) })), canDeleteInvoiceDraft && (_jsx("li", { children: _jsxs("a", Object.assign({ onClick: () => setBatchUnbillOpen(true) }, { children: [_jsx("i", { className: "fa fa-pencil-square-o" }), ' ', _jsx("i", { className: "fa fa-arrow-left margin5R" }), _jsx("span", { children: intl.formatMessage(messages.RevertToRegistrationsAction) })] })) }))] }))] })))] })) })));
    return (_jsxs("div", { children: [_jsxs(SimplePanel, Object.assign({ article: "5434858821650", title: intl.formatMessage(messages.TimeRegistrations), buttons: addMenu }, { children: [_jsx(RegistrationKpiHeader, { kpis: kpis }), _jsx(RegistrationOverviewFilters, { state: filterState, onChange: handleFilterChange, onApplyFilters: handleApplyFilters, total: total, selectedCount: selectedCount })] })), _jsxs("div", Object.assign({ className: "container-fluid" }, { children: [toolbar, _jsx("div", Object.assign({ className: "row" }, { children: _jsxs(ScrollableTable, Object.assign({ tableClassName: "table-grid scrollable-table", loadItems: loadItems, initialSearchParams: initialSearchParams, items: rows, renderTBody: true, renderRow: (item) => (_jsx(RegistrationOverviewRow, { registration: item, columnStatus: columnStatus, selected: !!item.selected, onToggleSelect: handleToggleSelect, onEdit: openEdit, onToggleBillable: handleToggleBillable, onToggleInForfait: handleToggleInForfait, onToggleNeverInvoice: handleToggleNeverInvoice, onAddToDraft: handleAddToDraft, onRemoveFromDraft: setRemoveFromDraftTarget, onToggleLock: handleToggleLock, onDelete: handleDelete, onOpenTaskDetail: reg => setTaskDetail({ id: reg.plannedTaskId }), permissions: permissions, manipulatingDrafts: manipulatingDrafts, currentUserId: currentUserId, canEditTask: canEditTask }, item.id)), noDataLabel: intl.formatMessage(messages.NoResults) }, { children: [_jsx(ColumnHeader, { title: _jsx(Checkbox, { value: selectAll, onClick: handleSelectAll }) }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.Date), sortKey: "date,fromMinutes", visible: columnStatus.date }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.ReferenceYear), sortKey: "referenceYear", visible: columnStatus.referenceYear }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.User), sortKey: "userId", visible: columnStatus.user }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.Relation), sortKey: "relation.displayName", visible: columnStatus.relation }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.CustomerCode), sortKey: "relation.customerCode", visible: columnStatus.customerCode }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.Assignment), sortKey: "invoiceStream.assignmentTemplateName,invoiceStream.assignmentName", visible: columnStatus.assignment }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.Type), visible: columnStatus.resourceType, filter: {
                                        type: 'items',
                                        key: 'type',
                                        items: [0, 1, 2, 3, 4, 5],
                                        renderItem: renderResourceType,
                                    } }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.Resource), visible: columnStatus.resource }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.Training), visible: columnStatus.trainingName }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.Task), visible: columnStatus.taskName }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.From), sortKey: "fromMinutes", className: "text-right", visible: columnStatus.fromMinutes }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.To), sortKey: "toMinutes", className: "text-right", visible: columnStatus.toMinutes }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.Quantity), sortKey: "durationMinutes,quantity", className: "text-right", visible: columnStatus.quantity }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.UnitPrice), sortKey: "amount", className: "text-right", visible: columnStatus.unitPrice }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.VatPercent), sortKey: "vat", className: "text-right", visible: columnStatus.vatPercentage }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.Amount), sortKey: "totalAmount", className: "text-right", visible: columnStatus.totalAmount }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.IsFixedPrice), sortKey: "isFixedPrice", className: "text-center", visible: columnStatus.isFixedPrice, filter: {
                                        type: 'items',
                                        key: 'isFixedPrice',
                                        items: [true, false],
                                        renderItem: renderYesNo,
                                    } }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.Billable), sortKey: "isBillable", className: "text-center", visible: columnStatus.isBillable, filter: {
                                        type: 'items',
                                        key: 'isBillable',
                                        items: [true, false],
                                        renderItem: renderYesNo,
                                    } }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.InForfait), sortKey: "forfaitStatus", className: "text-center", visible: columnStatus.forfaitStatus, filter: {
                                        type: 'items',
                                        key: 'forfaitStatus',
                                        items: [2, 1, 0],
                                        renderItem: renderForfait,
                                    } }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.InvoiceInForfait), sortKey: "invoiceForfaitStatus", className: "text-center", visible: columnStatus.invoiceForfaitStatus, filter: {
                                        type: 'items',
                                        key: 'invoiceForfaitStatus',
                                        items: [2, 1, 0],
                                        renderItem: renderForfait,
                                    } }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.NeverInvoice), sortKey: "neverInvoice", className: "text-center", visible: columnStatus.neverInvoice, filter: {
                                        type: 'items',
                                        key: 'neverInvoice',
                                        items: [true, false],
                                        renderItem: renderYesNo,
                                    } }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.Invoice), visible: columnStatus.invoiceStatus, filter: {
                                        type: 'items',
                                        key: 'invoiceStatus',
                                        items: [0, 1, 2, 3, 4, -1],
                                        renderItem: renderInvoiceStatusItem,
                                    } }), _jsx(ColumnHeader, { title: "", visible: columnStatus.remark }), _jsx(ColumnHeader, { title: intl.formatMessage(messages.Actions), className: "text-right" })] })) }))] })), modal && (_jsx(RegistrationModal, { show: true, registration: modal.registration, registrationOptions: modal.registrationOptions, onSave: handleModalSave, onClose: () => setModal(null) })), groupModal && (_jsx(RegistrationGroupModal, { show: true, registration: groupModal.registration, registrations: groupModal.registrations, onSave: () => {
                    setGroupModal(null);
                    reloadTable();
                }, onClose: () => setGroupModal(null) })), deleteTarget && (_jsx(YesNoModal, { show: true, title: intl.formatMessage(messages.ConfirmDeleteTitle), message: intl.formatMessage(messages.ConfirmDeleteRegistration), onNo: () => setDeleteTarget(null), onSave: confirmDelete })), removeFromDraftTarget && (_jsx(DeleteWithNeverInvoiceModal, { show: true, message: intl.formatMessage(messages.ConfirmUndoOrNeverInvoiceDetailLine), onResolve: resolveRemoveFromDraft })), showColumnModal && (_jsx(CheckboxListModal, { show: true, title: intl.formatMessage(messages.SelectTableColumns), items: hideableColumns, sortByLabel: false, onSave: handleColumnSelection, onClose: () => setShowColumnModal(false) })), batchDeleteOpen && (_jsx(YesNoModal, { show: true, title: intl.formatMessage(messages.ConfirmDeleteTitle), message: intl.formatMessage(messages.ConfirmDeleteRegistrations, {
                    count: selectedCount,
                }), onNo: () => setBatchDeleteOpen(false), onSave: confirmBatchDelete })), batchLockOpen && (_jsx(YesNoModal, { show: true, title: intl.formatMessage(messages.ConfirmTitle), message: intl.formatMessage(batchLockOpen.lock
                    ? messages.ConfirmLockRegistrations
                    : messages.ConfirmUnlockRegistrations, { count: selectedCount }), onNo: () => setBatchLockOpen(null), onSave: confirmBatchLock })), batchBillOpen && (_jsx(YesNoModal, { show: true, title: intl.formatMessage(messages.ConfirmTitle), message: intl.formatMessage(messages.ConfirmBillRegistrations), onNo: () => setBatchBillOpen(false), onSave: confirmBatchBill })), batchUnbillOpen && (_jsx(DeleteWithNeverInvoiceModal, { show: true, message: intl.formatMessage(messages.ConfirmUndoOrNeverInvoiceRegistrations), onResolve: resolveBatchUnbill })), batchUpdateOpen && currentTableParamsRef.current && currentServerFiltersRef.current && (_jsx(BatchUpdateRegistrationFieldModal, { show: true, selection: getBatchSelectionParams(), tableParams: currentTableParamsRef.current, filters: currentServerFiltersRef.current, onClose: () => setBatchUpdateOpen(false), onSaved: handleBatchUpdateSaved })), detailReportOpen && currentServerFiltersRef.current && (_jsx(RegistrationInvoiceDetailReportModal, { show: true, relation: filterState.relation, filters: currentServerFiltersRef.current, batchParams: getBatchSelectionParams(), onClose: () => setDetailReportOpen(false) })), taskDetail && (_jsx(TaskDetailsModal, { show: true, task: taskDetail, onClose: () => setTaskDetail(null), onSave: () => setTaskDetail(null), showSendEmailButton: false }))] }));
};
export default RegistrationOverviewContainer;
